import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Link as ChakraLink,
  Select,
  SimpleGrid,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { CheckCircleIcon, LockIcon } from '@chakra-ui/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../compnents/Navbar';
import { useTranslation } from '../i18n';
import Footer from '../compnents/Footer';
import AuthContext from '../context/AuthContext';
import { buildPurchaseIntentUrl, normalizePurchaseIntent } from '../utils/purchaseIntent';

// Role names and the three rows below are translation keys, not copy: the
// page is the first screen a Spanish adviser sees after verifying the email,
// and a role list in English there undoes the Spanish funnel that led here.
const employeeRoles = [
  { key: 'rm', titleKey: 'login.role.rm', descriptionKey: 'login.role.rm.desc' },
  { key: 'portfolio_adviser', titleKey: 'login.role.portfolio', descriptionKey: 'login.role.portfolio.desc' },
  { key: 'compliance', titleKey: 'login.role.compliance', descriptionKey: 'login.role.compliance.desc' },
  { key: 'head', titleKey: 'login.role.head', descriptionKey: 'login.role.head.desc' },
];

const darkInputProps = {
  bg: 'rgba(255, 255, 255, 0.05)',
  color: 'white',
  borderColor: 'brand.quantumBorder',
  _placeholder: { color: 'whiteAlpha.500' },
  _hover: { borderColor: 'brand.quantumAccent' },
  _focus: {
    borderColor: 'brand.quantumAccent',
    boxShadow: '0 0 0 1px var(--chakra-colors-brand-quantumAccent)',
  },
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [employeeMode, setEmployeeMode] = useState(false);
  const [role, setRole] = useState('rm');
  const [adminEmail, setAdminEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, authError } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { t } = useTranslation();
  const purchaseIntent = useMemo(
    () => normalizePurchaseIntent(location.search),
    [location.search]
  );
  const registerHref = buildPurchaseIntentUrl('/register', purchaseIntent);

  const isProduction = process.env.NODE_ENV === 'production';
  const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

  const selectedRole = useMemo(
    () => employeeRoles.find((entry) => entry.key === role) || employeeRoles[0],
    [role]
  );
  const isSubmitDisabled = !email.trim() || !password.trim() || (employeeMode && !adminEmail.trim());

  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
      setEmployeeMode(false);
      setAdminEmail('');
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = employeeMode
        ? await axios.post(
          `${API_BASE_URL}/auth/login-others`,
          { role, email, password, adminEmail },
          { headers: { 'Content-Type': 'application/json' } }
        )
        : await axios.post(
          `${API_BASE_URL}/auth/login`,
          { email, password },
          { headers: { 'Content-Type': 'application/json' } }
        );

      const data = response.data;

      if (data.token) {
        login(data.token, null);
        toast({
          title: t('login.toast.ok.title'),
          description: employeeMode
            ? t('login.toast.ok.role').replace('{role}', t(selectedRole.titleKey).toLowerCase())
            : t('login.toast.ok.body'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        navigate(
          !employeeMode && purchaseIntent.plan
            ? buildPurchaseIntentUrl('/subscription', purchaseIntent)
            : '/dashboard'
        );
      } else {
        toast({
          title: t('login.toast.failed.title'),
          description: data.message || t('login.toast.failed.body'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      let errorMessage = t('login.toast.error.body');

      if (error.response) {
        errorMessage = error.response.data.message || errorMessage;
      } else if (error.request) {
        errorMessage = t('login.toast.offline');
      }

      toast({
        title: t('login.toast.error.title'),
        description: errorMessage,
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="brand.quantumBg">
      <Navbar />

      <Box className="grid-bg-dark quantum-mesh" flex="1" overflow="hidden">
        <Container maxW="container.xl" position="relative" zIndex={1} py={{ base: 10, md: 16 }}>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 8, md: 10 }} alignItems="center">
            <VStack align="stretch" spacing={7}>
              <Badge
                alignSelf="flex-start"
                bg="rgba(0, 212, 170, 0.1)"
                color="brand.quantumAccent"
                borderRadius="full"
                px={3}
                py={1}
                letterSpacing="0.15em"
                textTransform="uppercase"
              >
                {t('login.badge')}
              </Badge>

              <Box>
                <Heading as="h1" fontSize={{ base: '38px', md: '56px' }} color="white" lineHeight="1.05">
                  {t('login.hero.title')}
                </Heading>
                <Text mt={4} color="brand.quantumMuted" fontSize="lg" maxW="2xl">
                  {t('login.hero.body')}
                </Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                {[
                  t('login.point.one'),
                  t('login.point.two'),
                  t('login.point.three'),
                ].map((item) => (
                  <Box
                    key={item}
                    bg="brand.quantumCard"
                    borderWidth="1px"
                    borderColor="brand.quantumBorder"
                    borderRadius="2xl"
                    p={4}
                    boxShadow="0 14px 38px rgba(0, 0, 0, 0.24)"
                  >
                    <HStack align="start" spacing={3}>
                      <Icon as={CheckCircleIcon} color="brand.quantumAccent" mt={0.5} />
                      <Text color="white" fontSize="sm" fontWeight="700">
                        {item}
                      </Text>
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>

              <Box
                bg="rgba(17, 27, 46, 0.84)"
                borderRadius="2xl"
                borderWidth="1px"
                borderColor="brand.quantumBorder"
                p={{ base: 5, md: 6 }}
                boxShadow="0 24px 70px rgba(0, 0, 0, 0.35)"
              >
                <Text fontSize="sm" fontWeight="800" color="brand.quantumAccent" textTransform="uppercase" letterSpacing="0.15em" mb={4}>
                  {t('login.after.title')}
                </Text>
                <VStack align="stretch" spacing={3}>
                  {[
                    { label: t('login.after.queue.label'), value: t('login.after.queue.value'), meta: t('login.after.queue.meta') },
                    { label: t('login.after.evidence.label'), value: t('login.after.evidence.value'), meta: t('login.after.evidence.meta') },
                    { label: t('login.after.drift.label'), value: t('login.after.drift.value'), meta: t('login.after.drift.meta') },
                  ].map((row) => (
                    <Box key={row.label} bg="rgba(255, 255, 255, 0.045)" borderRadius="xl" p={4} borderWidth="1px" borderColor="brand.quantumBorder">
                      <HStack justify="space-between" align="start" spacing={4} flexWrap="wrap">
                        <Box>
                          <Text color="brand.quantumSubtle" fontSize="xs" textTransform="uppercase" fontWeight="800" letterSpacing="0.12em">
                            {row.label}
                          </Text>
                          <Text color="white" fontWeight="800" mt={1}>
                            {row.value}
                          </Text>
                        </Box>
                        <Text color="brand.quantumAccent" fontSize="sm" fontWeight="700" textAlign={{ base: 'left', md: 'right' }}>
                          {row.meta}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </Box>
            </VStack>

            <Box
              bg="brand.quantumCard"
              p={{ base: 6, md: 8 }}
              borderRadius="2xl"
              boxShadow="0 24px 80px rgba(0, 0, 0, 0.38)"
              borderWidth="1px"
              borderColor="brand.quantumBorder"
            >
              <VStack spacing={6} align="stretch">
                <Box>
                  <Heading as="h2" size="lg" color="white">
                    {t('login.card.title')}
                  </Heading>
                  <Text mt={2} color="brand.quantumMuted">
                    {t('login.card.body')}
                  </Text>
                </Box>

                {authError ? (
                  <Alert status="warning" borderRadius="lg" alignItems="flex-start">
                    <AlertIcon mt={1} />
                    <Box>
                      <AlertTitle fontSize="sm">{t('login.alert.session.title')}</AlertTitle>
                      <AlertDescription fontSize="sm">
                        {authError}. {t('login.alert.session.body')}
                      </AlertDescription>
                    </Box>
                  </Alert>
                ) : null}

                {location.state?.verified ? (
                  <Alert status="success" borderRadius="lg" alignItems="flex-start">
                    <AlertIcon mt={1} />
                    <Box>
                      <AlertTitle>{t('login.alert.verified.title')}</AlertTitle>
                      <AlertDescription>
                        {t('login.alert.verified.body')}
                      </AlertDescription>
                    </Box>
                  </Alert>
                ) : null}

                <form onSubmit={handleSubmit}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel fontWeight="700" color="white">{t('login.field.email')}</FormLabel>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        size="lg"
                        {...darkInputProps}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <HStack justify="space-between" align="center">
                        <FormLabel fontWeight="700" color="white" mb={0}>{t('login.field.password')}</FormLabel>
                        <ChakraLink as={Link} to="/reset-password" color="brand.quantumAccent" fontWeight="700" fontSize="sm" _hover={{ color: 'brand.quantumCyan' }}>
                          {t('login.field.forgot')}
                        </ChakraLink>
                      </HStack>
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('login.field.passwordPlaceholder')}
                        size="lg"
                        mt={2}
                        {...darkInputProps}
                      />
                    </FormControl>

                    <Box w="full" bg="rgba(255, 255, 255, 0.04)" borderWidth="1px" borderColor="brand.quantumBorder" borderRadius="xl" p={4}>
                      <Checkbox
                        isChecked={employeeMode}
                        onChange={(event) => {
                          setEmployeeMode(event.target.checked);
                          if (!event.target.checked) setAdminEmail('');
                        }}
                        colorScheme="teal"
                      >
                        <Text as="span" color="white" fontWeight="700">
                          {t('login.employee.toggle')}
                        </Text>
                      </Checkbox>
                      <Text color="brand.quantumMuted" fontSize="sm" mt={2}>
                        {t('login.employee.help')}
                      </Text>

                      {employeeMode ? (
                        <VStack spacing={4} align="stretch" mt={4}>
                          <FormControl isRequired>
                            <FormLabel fontWeight="700" color="white">{t('login.field.role')}</FormLabel>
                            <Select
                              value={role}
                              onChange={(e) => setRole(e.target.value)}
                              size="lg"
                              {...darkInputProps}
                            >
                              {employeeRoles.map((entry) => (
                                <option key={entry.key} value={entry.key} style={{ color: '#0f172a' }}>
                                  {t(entry.titleKey)}
                                </option>
                              ))}
                            </Select>
                            <Text color="brand.quantumMuted" fontSize="sm" mt={2}>
                              {t(selectedRole.descriptionKey)}
                            </Text>
                          </FormControl>

                          <FormControl isRequired>
                            <FormLabel fontWeight="700" color="white">{t('login.field.adminEmail')}</FormLabel>
                            <Input
                              type="email"
                              value={adminEmail}
                              onChange={(e) => setAdminEmail(e.target.value)}
                              placeholder="admin@company.com"
                              size="lg"
                              {...darkInputProps}
                            />
                          </FormControl>
                        </VStack>
                      ) : null}
                    </Box>

                    <Button
                      type="submit"
                      width="full"
                      mt={1}
                      isLoading={loading}
                      isDisabled={isSubmitDisabled}
                      loadingText={t('login.cta.loading')}
                      bgGradient="linear(135deg, brand.quantumAccent, brand.quantumCyan)"
                      color="brand.quantumBg"
                      _hover={{ opacity: 0.9, transform: 'translateY(-1px)' }}
                      fontSize="md"
                      fontWeight="800"
                      py={6}
                      leftIcon={<LockIcon />}
                    >
                      {t('login.cta.submit')}
                    </Button>

                    <Text textAlign="center" color="brand.quantumMuted">
                      {t('login.newHere')}{' '}
                      <ChakraLink as={Link} to={registerHref} color="brand.quantumAccent" fontWeight="800" _hover={{ color: 'brand.quantumCyan' }}>
                        {t('login.register')}
                      </ChakraLink>
                    </Text>
                  </VStack>
                </form>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default LoginPage;
